import React, { useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Users, LayoutTemplate, Send, Settings,
  Zap, LogOut, Check, AlertCircle, Info, Mail, MessageCircle, Camera, GraduationCap
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { ToastType, TemplateType } from '../types';

/* Toast */
export function Toast({ msg, type }: { msg: string; type: ToastType }): React.ReactElement {
  const icons = {
    success: <Check size={15} color="var(--accent3)" />,
    error:   <AlertCircle size={15} color="var(--accent2)" />,
    info:    <Info size={15} color="var(--accent)" />,
  };
  return (
    <div style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 999, pointerEvents: 'none' }}>
      <div className={`toast ${type}`} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>{icons[type]}<span>{msg}</span></div>
    </div>
  );
}

export function useToast() {
  const [toast, setToast] = useState<{ msg: string; type: ToastType } | null>(null);
  const show = useCallback((msg: string, type: ToastType = 'info') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  }, []);
  return { toast, show };
}

/* Sidebar */
export function Sidebar(): React.ReactElement {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const nav = [
    { path: '/app/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/app/leads',     icon: Users,           label: 'Leads' },
    { path: '/app/templates', icon: LayoutTemplate,  label: 'Templates' },
    { path: '/app/send',      icon: Send,            label: 'Send Message' },
    { path: '/app/settings',  icon: Settings,        label: 'Settings' },
  ];
  const initials = user?.displayName
    ? user.displayName.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase()
    : (user?.email?.[0] ?? '?').toUpperCase();

  return (
    <aside style={{
      width: 'var(--sidebar-w)', height: '100vh', position: 'fixed', left: 0, top: 0,
      background: 'var(--surface)', borderRight: '1px solid var(--border)',
      display: 'flex', flexDirection: 'column', zIndex: 100,
    }}>
      <div style={{ padding: '24px 18px 20px', borderBottom: '1px solid var(--border)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 34, height: 34, background: 'var(--accent)', borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 14px rgba(108,99,255,0.4)' }}>
            <Zap size={18} color="#fff" fill="#fff" />
          </div>
          <div>
            <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: 16 }}>LeadFlow</div>
            <div style={{ fontSize: 10, color: 'var(--text3)', letterSpacing: '0.8px', textTransform: 'uppercase' }}>by Webiconic</div>
          </div>
        </div>
      </div>
      <nav style={{ flex: 1, padding: '14px 10px', overflow: 'auto' }}>
        {nav.map(({ path, icon: Icon, label }) => {
          const active = pathname.startsWith(path);
          return (
            <button key={path} onClick={() => navigate(path)} style={{
              width: '100%', display: 'flex', alignItems: 'center', gap: 10,
              padding: '10px 12px', borderRadius: 9, border: 'none', marginBottom: 2,
              background: active ? 'var(--accent-dim)' : 'transparent',
              color: active ? 'var(--accent)' : 'var(--text2)',
              fontSize: 14, fontWeight: active ? 600 : 400,
              transition: 'all 0.15s', cursor: 'pointer',
              borderLeft: `2px solid ${active ? 'var(--accent)' : 'transparent'}`,
            }}>
              <Icon size={16} /><span>{label}</span>
            </button>
          );
        })}
      </nav>
      <div style={{ padding: '14px', borderTop: '1px solid var(--border)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
          {user?.photoURL
            ? <img src={user.photoURL} alt="" style={{ width: 32, height: 32, borderRadius: '50%' }} />
            : <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: '#fff' }}>{initials}</div>
          }
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.displayName || 'User'}</div>
            <div style={{ fontSize: 11, color: 'var(--text3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.email}</div>
          </div>
        </div>
        <button className="btn btn-ghost btn-sm btn-full" onClick={logout} style={{ justifyContent: 'center' }}>
          <LogOut size={13} /> Sign Out
        </button>
        <br /><br />
        <button className="btn btn-ghost btn-sm btn-full" onClick={() => navigate('/student/cvs')} style={{ justifyContent: 'center', color: 'var(--accent)', borderColor: 'rgba(108,99,255,0.3)', background: 'rgba(108,99,255,0.05)' }}>
          <GraduationCap size={13} /> Student Mode
        </button>
      </div>
    </aside>
  );
}

/* Badges */
export function CategoryBadge({ name, color }: { name: string; color?: string }): React.ReactElement {
  return (
    <span className="badge" style={{
      background: color ? `${color}18` : 'var(--surface3)',
      border: `1px solid ${color ? `${color}40` : 'var(--border)'}`,
      color: color || 'var(--text2)',
    }}>{name}</span>
  );
}

export function TypeBadge({ type }: { type: TemplateType }): React.ReactElement {
  const map: Record<TemplateType, { icon: React.ReactElement; label: string }> = {
    email:     { icon: <Mail size={10} />,         label: 'Email' },
    whatsapp:  { icon: <MessageCircle size={10} />, label: 'WhatsApp' },
    instagram: { icon: <Camera size={10} />,       label: 'Instagram' },
  };
  const { icon, label } = map[type];
  return <span className={`badge badge-${type}`}>{icon} {label}</span>;
}

/* Spinner */
export function Spinner({ size = 18 }: { size?: number }): React.ReactElement {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className="spin" style={{ flexShrink: 0 }}>
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeOpacity="0.2" />
      <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

/* Confirm Modal */
export function ConfirmModal({ title, message, onConfirm, onCancel, danger }: {
  title: string; message: React.ReactNode;
  onConfirm: () => void; onCancel: () => void; danger?: boolean;
}): React.ReactElement {
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onCancel()}>
      <div className="modal" style={{ maxWidth: 400 }}>
        <div className="modal-header">
          <h3 style={{ fontSize: 17 }}>{title}</h3>
          <button className="btn btn-ghost btn-sm" onClick={onCancel}>✕</button>
        </div>
        <div className="modal-body">
          <p style={{ color: 'var(--text2)', fontSize: 14, lineHeight: 1.6 }}>{message}</p>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onCancel}>Cancel</button>
          <button
            className="btn btn-primary"
            style={danger ? { background: 'var(--accent2)', boxShadow: 'none' } : {}}
            onClick={onConfirm}
          >Confirm</button>
        </div>
      </div>
    </div>
  );
}